/**
 * Event page.
 *
 * @module pages/EventPage
 */
import _ from 'lodash';
import BasePage from './BasePage';
import BodyStateAdapter from '../components/BodyStateAdapter';
import CastMarket from '../components/CastMarket';
import EventDataProcessor from '../components/EventDataProcessor';
import jQuery from 'jquery';
import MessageBusTopics from '../../common/MessageBusTopics';
import MinuteMarkets from '../components/MinuteMarkets';
import ScoreboardAdapter from '../views/ScoreboardAdapter';
import SelectionViewAdapter from '../views/SelectionViewAdapter';
import SwiperNavigation from '../components/SwiperNavigation';
import Tabs from '../components/Tabs';
import UrlArgsProvider from '../components/UrlArgsProvider';
import URLProvider from '../components/URLProvider';
import LivescoreDiffusionAdapter from '../components/push/LivescoreDiffusionAdapter';
import EnhancedOddsDiffusionAdapter from '../components/push/EnhancedOddsDiffusionAdapter';
import HiddenMarketsWidget from '../components/HiddenMarketsWidget';
import { renderNewBanners } from '../components/newBanners/newBanners';
import EventViewAdapter from '../views/EventViewAdapter';

var $scoreboards = null;
const getConsole = () => window.consoleReference || window.console;

const POPULAR_BET_BUILDERS_HUB = 'popular-bet-builder-hub-component-football';
const MARKETMENUCONTAINER = 'markets-container';

const betBuilderObject = {
    selector: POPULAR_BET_BUILDERS_HUB,
    id: 'POPULAR_BET_BUILDERS_HUB',
    metadata: {},
    sport: 'football',
    headerDisabled: true,
};


/**
 * The subscription token to be able to un-subscribe the event on demand.
 *
 * @type {string}
 */
var beforePageChangeSubscription = null;

/**
 * Global PubSub reference
 *
 * @type {Object}
 *
 * @private
 */
var pubSub = window.WH.messageBus;

/**
 * Internal state flag used to
 * determine whether page has been
 * rendered or not
 *
 * @type {Boolean}
 */
var rendered = false;

/**
 * Page dependencies
 *
 * @type {Object}
 */
var deps = null;

/**
 * Page content in the form of a jQuery element
 *
 * @type {Object}
 */
var content = null;

/**
 * Map object between market groups and all collections it's in
 * [marketGroupId]: [...marketCollectionIds]
 *
 * @type {Object}
 */
var marketGroupsCollections = null;

/**
 * Pagination
 *
 * @type {Object}
 */
var pagination = {

    /**
     * Base link
     *
     * @type {String}
     */
    baseLink: null,

    /**
     * XMLHttpRequest
     *
     * @type {XMLHttpRequest}
     */
    request: null,

    /**
     * Row IDs
     *
     * @type {Array}
     */
    rowIds: [],

    /**
     * Abort Ajax request if needed
     *
     * @returns {void}
     *
     * @private
     */
    abort: function () {

        if (pagination.request && typeof pagination.request.abort === 'function') {
            pagination.request.abort();
        }
    },

    /**
     * Lazy Loading initialize
     *
     * @returns {void}
     *
     * @private
     */
    init: function () {

        if (window.WH.sportsbook.totalPages > 1) {
            BasePage.LazyLoadingViewAdapter.init({
                totalPages: window.WH.sportsbook.totalPages,
                container: '#eventEntity',
                group: '.col-inner',
                subGroup: '#markets-container',
                row: '.events__market-container',
                selection: '.market__button, .oddsbutton',
                selectionOdds: '.selectiondetails, .betbutton__odds',
                //replaceGroupStr: '<!-- subHeaderPlaceholder -->',
                //replaceSubGroupStr: '<!-- eventPlaceholder -->',
                totalItemsPerPage: window.WH.sportsbook.eventsPerPage,
                pagination: pagination,
                highlightedSelections: SelectionViewAdapter.highlightedSelections,
                timeSelector: '.localisable',
                totalRowsCount: window.WH.sportsbook.totalRowsCount,
            });
        }
    },

    /**
     * Gets more data to Lazy Load
     *
     * @param {Number}   rowNumber
     * @param {Number}   direction
     * @param {Function} callback
     *
     * @returns {void}
     *
     * @private
     */
    getMoreData: function (rowNumber, direction, callback) {

        var pageNumber = Math.ceil(rowNumber / parseInt(window.WH.sportsbook.eventsPerPage, 10));

        if (pageNumber > 0 && pageNumber <= parseInt(window.WH.sportsbook.totalPages, 10)) {
            pagination.getPage(rowNumber, direction, callback);
        } else {
            callback({ totalRows: 0, compiled: [] }, direction);
        }
    },

    /**
     * Get data for selected page
     *
     * @param   {Number}   rowNumber
     * @param   {Number}   direction
     * @param   {Function} callback
     *
     * @returns {void}
     *
     * @private
     */
    getPage: function (rowNumber, direction, callback) {

        var pageNumber = Math.ceil(rowNumber / parseInt(window.WH.sportsbook.eventsPerPage, 10));

        if (deps && deps.pageCommon && direction === 1) {

            pagination.request = deps.pageCommon.getJSONResource(
                pagination.baseLink + pageNumber + '.json',
                {
                    'afterSuccess': pagination.getPageSuccess.bind(this, callback, rowNumber),
                    'afterError': callback.bind(this, { totalRows: 0, compiled: [] }, direction),
                }
            );
        }
    },

    /**
     * Function to filter already existing data
     *
     * @param   {Object}   data
     * @param   {Number}   rowNumber
     * @param   {Number}   direction
     *
     * @returns {void}
     *
     * @private
     */
    filterPaginationData: function (data, rowNumber, direction) {

        var marketsToFilter = 0;

        if (direction === -1) {
            marketsToFilter = ((rowNumber - 1) % parseInt(window.WH.sportsbook.eventsPerPage, 10)) + 1;
            marketsToFilter = Math.min(parseInt(window.WH.sportsbook.eventsPerPage, 10),
                Object.keys(data.data.model.markets).length) - marketsToFilter;
        } else {
            marketsToFilter = ((rowNumber - 1) % parseInt(window.WH.sportsbook.eventsPerPage, 10));
        }

        pagination.removeCompiledRows(
            data,
            marketsToFilter,
            direction
        );
    },

    /**
     * Remove compiled rows that we don't need
     *
     * @param   {Object} data
     * @param   {Array} marketsToFilter
     * @param   {Number} direction
     *
     * @returns {void}
     *
     * @private
     */
    removeCompiledRows: function (data, marketsToFilter, direction) {

        /**
         * A set of helper functions
         *
         * @type {Object}
         */
        var helperFunctions = {

            /**
             * Remove rows
             *
             * @param   {Object} row
             *
             * @returns {Boolean}
             *
             * @private
             */
            rowsRemove: function (row) {

                return !_.includes(rowsToRemove, row.key);
            },
        };

        if (marketsToFilter > 0) {
            pagination.rowIds = [];
            var rowsToRemove = pagination.rowsToRemove(data, marketsToFilter, direction);

            data.data.compiled[0].rows[0].rows = _.filter(
                data.data.compiled[0].rows[0].rows, helperFunctions.rowsRemove
            );
        }
    },

    /**
     * Loop over subgroups
     *
     * @param   {Object} group
     *
     * @returns {void}
     *
     * @private
     */
    groupRows: function (group) {

        _.each(group.rows, pagination.subgroupRowIds);
    },

    /**
     * Loop over rows
     *
     * @param   {Object} subgroup
     *
     * @returns {void}
     *
     * @private
     */
    subgroupRowIds: function (subgroup) {

        pagination.rowIds = pagination
            .rowIds.concat(_.map(subgroup.rows, 'key'));
    },

    /**
     * Obtain row IDs given the compiled Array
     *
     * @param   {Array} compiled
     *
     * @returns {void}
     *
     * @private
     */
    obtainRowIds: function (compiled) {

        _.each(compiled, pagination.groupRows);
    },

    /**
     * Select rows to be removed
     *
     * @param   {Object} data
     * @param   {Array} marketsToFilter
     * @param   {Number} direction
     *
     * @returns {void}
     *
     * @private
     */
    rowsToRemove: function (data, marketsToFilter, direction) {

        pagination.obtainRowIds(data.data.compiled);

        if (direction === -1) {
            pagination.rowIds = pagination.rowIds.splice(-1 * marketsToFilter);
        } else {
            pagination.rowIds = pagination.rowIds.splice(0, marketsToFilter);
        }

        _.each(pagination.rowIds, function (marketId) {

            delete data.data.model.markets[marketId];
        });

        return pagination.rowIds;
    },

    /**
     * Do model updates
     *
     * @param   {Object} model
     *
     * @returns {void}
     *
     * @private
     */
    doModelUpdates: function (model) {

        BasePage.ModelComposer.extendModel(model);
        Tabs.init();
        SwiperNavigation.initializeAllSwipers();
    },

    /**
     * Function called after obtaining next page data to parse it and save it
     *
     * @param   {Function} callback
     * @param   {Number}   rowNumber
     * @param   {Object}   data
     *
     * @returns {void}
     *
     * @private
     */
    saveNextPage: function (callback, rowNumber, data) {

        pagination.filterPaginationData(data, rowNumber, 1);

        pagination.doModelUpdates(data.data.model);

        /**
         * Subscribe to lazyloaded markets based on modelBox item
         *
         * @param {String} type 'markets' or 'selections'
         * @param {Object} hashedEntity Model item in hashed format
         * @param {String} hashedEntityId Market or selection id
         *
         * @returns {void}
         */
        var subscribeToEntity = function subscribeToEntity(type, hashedEntity, hashedEntityId) {

            push.subscribeEntityToPush(window.WH.sportsbook.model[type][hashedEntityId]);
        };
        _.each(
            data.data.model.markets,
            subscribeToEntity.bind(null, 'markets')
        );

        _.each(
            data.data.model.selections,
            subscribeToEntity.bind(null, 'selections')
        );

        if (typeof callback === 'function') {
            callback({
                totalRows: data.data.compiled[0].rows[0].rows.length,
                compiled: data.data.compiled,
            }, 1);
        }
    },

    /**
     * Function to handle a success response from fetching a page, but that does not contain any data.
     * This can occur when the server has an unhandled timeout for example.
     *
     * @param callback A function to call with the result of saving: nr of rows and compiled data
     *
     * @returns {void}
     *
     * @private
     */
    handleEmptyPageData: function (callback) {

        if (typeof callback === 'function') {

            callback({
                totalRows: 0,
                compiled: [],
            }, 1);
        }
    },

    /**
     * Function called after a new page was successfully fetched. It handles the scenario where we have a
     * success response but might be empty, due to timeouts on the server for example.
     *
     * @param {Function} callback A function to call with the result of saving: nr of rows and compiled data
     * @param {integer} rowNumber The row number this page starts from
     * @param {Object} data Fetched from the server representing the page (model, pointers, compiled ...)
     *
     * @returns {void}
     *
     * @private
     */
    getPageSuccess: function (callback, rowNumber, data) {

        if (!_.isEmpty(data) && !_.isEmpty(data.data)) {

            pagination.saveNextPage(callback, rowNumber, data);
        } else {

            pagination.handleEmptyPageData(callback);
        }
    },

    /**
     * Initializes pagination
     *
     * @returns {void}
     *
     * @private
     */
    initializePagination: function () {

        pagination.baseLink = window.WH.sportsbook.baseLink;
        pagination.init();
    },


    /**
     * Destroy pagination pagination
     *
     * @returns {void}
     *
     * @private
     */
    destroy: function () {

        BasePage.LazyLoadingViewAdapter.destroy();
    },

    /**
     * This functions handles removing markets from model
     *
     * @param   {Array} marketsToRemove
     *
     * @returns {void}
     *
     * @private
     */
    updateModel: function (marketsToRemove) {

        BasePage.ModelComposer.removeMarketsFromModel(marketsToRemove);
    },
};

/**
 * Init the model
 *
 * @param   {Boolean} regenerateModel
 *
 * @returns {void}
 *
 * @private
 */
var initModel = function (regenerateModel) {

    if (regenerateModel === true) {
        BasePage.ModelComposer.regenerateMergedModel();
    }
};

const checkIfCountryIsAllowed = function () {
    const allowedCountries = ['GB', 'IE', 'GI'];
    const match = document.cookie.match(/(?:^|;\s*)country=([^;]*)/);
    return match ? allowedCountries.includes(match[1]) : false;
};

const getBuilderReady = function () {
    const sportName = window.WH.sportsbook.sportEn === 'football';
    const page = window.WH.sportsbook.page === 'events';
    return sportName && page && checkIfCountryIsAllowed();
};

const checkIfSpain = () => {
    return window.WH.sportsbook?.lang == 'es-es';
};

const startBetBuilderWidgetFootball = () => {
    if (!checkIfSpain()) {
        import('@sb/popular-bet-builders/dist/popular-bet-builders.widget')
            .then(() => {
                window.WH.messageBus.publish(MessageBusTopics.START_POPULAR_BET_BUILDERS_HUB, betBuilderObject);
            })
            .catch(err => {
                getConsole().error('WidgetPopularBetBuilders loading error', err);
            });
    }
};

const stopAccaWidget = () => {
    window.WH.messageBus.publish(MessageBusTopics.STOP_POPULAR_BET_BUILDERS_HUB, betBuilderObject);
};

const removePopularBetBuilderDiv = () => {
    const elementToCheck = document.getElementById('popular-bet-builder-hub-component-football');
    const elementToRemove = document.getElementById('popular-bet-builder-wraper');

    if (!elementToCheck || !elementToRemove) return;
    elementToRemove.remove();
};

const removeEmptyPopularAcca = () => {
    window.WH.messageBus.subscribe('wh.sb.widgetPopularBetBuilders.stop', removePopularBetBuilderDiv);
};

const openPopularBetBuilder = () => {
    waitForH2WithText('90 Minutes', (h2) => {
        appendDivToMarkets(); 
    });
};

const betBuilderListener = () => {
    window.WH.messageBus.subscribe('closeBuildYourOddsModal', openPopularBetBuilder);
};

function waitForH2WithText(text, callback, interval = 200, timeout = 5000) {
    const start = Date.now();
    const timer = setInterval(() => {
        const h2 = [...document.querySelectorAll('h2.fl')]
            .find(el => el.textContent.trim() === text);
        if (h2) {
            clearInterval(timer);
            callback(h2);
        } else if (Date.now() - start > timeout) {
            clearInterval(timer);
        }
    }, interval);
}

const appendDivToMarkets = function () {
    const h2 = [...document.querySelectorAll('h2.fl')]
        .find(el => el.textContent.trim() === '90 Minutes');

    if (h2) {
        const parentDiv = h2.closest('div');

        if (parentDiv) {
            const container = document.createElement('div');
            container.innerHTML = `
            <div data-status="A" id="popular-bet-builder-wraper"
                 class="event three-col-no-titles events__market-container"
                 data-betinrun="false" data-displayed="true">
              <section class="event-container scrollable -opened">
                <header tabindex="0" class="header-dropdown header-dropdown--large -expanded"
                        data-toggle-send="toggle-OB_MA1603563671Bet" aria-expanded="true">
                  <h2 class="fl">
                  <i class="popular-bet-builder-banner-icon"></i>Popular Bet Builders</h2>
                  <div class="header__side">
                    <div class="header__toolbar">
                      <a href="javascript:void(0)" class="button-clear" tabindex="-1" aria-hidden="true">
                        <i class="icon-arrow-up-slim if-expanded"></i>
                        <i class="icon-arrow-down-slim if-collapsed"></i>
                      </a>
                    </div>
                  </div>
                </header>
                <div class="btmarket__wrapper -expanded"
                     data-toggle-receive="toggle-OB_MA1603563671Bet"
                     style="display: block;" aria-expanded="true">
                     <div id="popular-bet-builder-hub-component-football"></div>
                </div>
              </section>
            </div>
            `;

            parentDiv.insertAdjacentElement('afterend', container);
            startBetBuilderWidgetFootball();
            removeEmptyPopularAcca();
        }
    }
};

/**
 * Handles menu functions
 *
 * @returns    {Object}
 *
 * @private
 */
var menuHandler = (function () {

    var selectedMenuId = -1;
    var loading = false;

    return {

        /**
         * Gets the loading status
         *
         * @returns {boolean}
         *
         * @private
         */
        getLoadingStatus: function () {

            return loading;
        },

        /**
         * Updates the loading status
         *
         * @param   {Boolean} status    loadding status
         *
         * @private
         */
        udpateLoadingStatus: function (status) {

            loading = status;
        },

        /**
         * Get the selected menu id
         *
         * @returns {Integer}
         *
         * @private
         */
        getSelectedMenuId: function () {

            return selectedMenuId;
        },

        /**
         * Updates the selected menu id
         *
         * @param   {Integer} id    menu id
         *
         * @returns {void}
         *
         * @private
         */
        updateSelectedMenuId: function (id) {

            selectedMenuId = id;
        },

        /**
         * Guesses the selected menu id from the DOM element
         *
         * @returns {Integer}
         *
         * @private
         */
        updateFromDOM: function () {

            var newSelection = BasePage
                .jQuery('#marketCollectionsMenu .filter-list__item a.-active')
                .data('marketcollectionid');

            selectedMenuId = (typeof newSelection === 'undefined') ? -1 : newSelection;
            if (getBuilderReady() && !checkIfSpain()) {
                if (selectedMenuId == 0 || selectedMenuId == -1) {
                    waitForH2WithText('90 Minutes', (h2) => {
                        appendDivToMarkets(); 
                    });
                } else {
                    stopAccaWidget();
                }
            }
            return selectedMenuId;
        },
    };
})();

/**
 * Init market collections menu
 *
 * @param   {Object} deps       Object that contains page
 *                              dependencies
 * @param   {Object} WHSports   WHSports data object
 *
 * @returns {void}
 *
 * @private
 */
var initMarketCollectionsMenu = function () {

    if (BasePage.jQuery('.exception-msg').length === 0) {
        updateMenuVisibility();
    }
};

/**
 * Function called after rendering the page
 *
 * @param   {Object} deps    Page dependencies
 * @param   {Object} content jQuery container element
 *
 * @returns {void}
 *
 * @public
 */
var initViews = function (deps, content) {

    initInternals(deps, content);

    handleSubscriptionsWithoutPushNotifications();
    SelectionViewAdapter
        .init(deps.sportsDataRepository.getModel().selections, content);

    BasePage.setOddsFormat();
    initDate();
    showBroadcast();
    menuHandler.updateFromDOM();
};

/**
 * Makes a DOM element visible
 *
 * @param   {String | Object}   selector    css selector string
 *                                          or jquery object
 *
 * @returns {void}
 *
 * @private
 */
const show = function (selector) {
    BasePage.jQuery(selector).removeClass('displayNone');
};

/**
 * Makes a DOM element hidden
 *
 * @param   {String | Object}   selector    css selector string
 *                                          or jquery object
 *
 * @returns {void}
 *
 * @private
 */
const hide = function (selector) {
    BasePage.jQuery(selector).addClass('displayNone');
};

/**
 * Makes a DOM element visible
 *
 * @param   {String | Object}   selector    css selector string
 *                                          or jquery object
 *
 * @returns {void}
 *
 * @private
 */
var showMatches = function (selector) {

    if (window.WH.sportsbook.showMatches) {
        BasePage.jQuery(selector).removeClass('displayNone');
    }
};

/**
 * Binds the click on Load More button
 *
 * @returns {void}
 *
 * @private
 */
var showMoreButton = function () {

    content.off('click.showMoreButton')
        .on('click.showMoreButton', '.show-more-button', showMoreSelections);
};

/**
 * Init internal data for later usage
 *
 * @param   {Object} deps    Page dependencies
 * @param   {Object} content jQuery container element
 *
 * @returns {void}
 *
 * @private
 */
var initInternals = function (deps, content) {

    initData(deps, content);
    pagination.initializePagination();
    Tabs.init();
    SwiperNavigation.initializeAllSwipers();
    CastMarket.init();
    BasePage.setOddsFormat();
};

/**
 * Init internal data for later usage
 *
 * @param   {Object} deps    Page dependencies
 * @param   {Object} content jQuery container element
 *
 * @returns {void}
 *
 * @private
 */
var initData = function (pageDeps, pageContent) {

    deps = pageDeps;
    content = pageContent;
};

/**
 * Subscribes to PDS
 *
 * @returns {void}
 *
 * @private
 */
var handleSubscriptions = function () {

    push.addOnTopicStatusFunction();
    push.subscribeToLiveScore();
    push.subscribeToEnahnacedOdds();
    push.subscribeVisibleEntities();
    minuteMarkets.subscribeToMinuteMarketsLifecycle();
};

/**
 *  Subscriptions to the SP page lifecycle events.
 *
 * @returns {void}
 *
 * @private
 */
var subscribePageLifecycleSubscriptions = function subscribePageLifecycleSubscriptions() {

    beforePageChangeSubscription = pubSub.subscribe(
        MessageBusTopics.NAVIGATION_BEFORE_PAGE_CHANGE, beforePageChange
    );
};

/**
 *  Removing the subscriptions to the SP page lifecycle events.
 *
 * @returns {void}
 *
 * @private
 */
var unSubscribePageLifecycleSubscriptions = function unSubscribePageLifecycleSubscriptions() {

    pubSub.unsubscribe(beforePageChangeSubscription);
    beforePageChangeSubscription = null;
};

/**
 * Subscribes to Lives Core and topic status changes
 *
 * @returns {void}
 *
 * @private
 */
var handleSubscriptionsWithoutPushNotifications = function () {

    push.addOnTopicStatusFunction();
    push.subscribeToLiveScore();
    push.subscribeToEnahnacedOdds();
};

/**
 * Formats a date
 *
 * @returns {void}
 *
 * @private
 */
var initDate = function () {

    var dateElement = content.find('.event-start-date');

    if (dateElement.length !== 0) {
        var startDate = dateElement.get(0).getAttribute('data-startdate');
        dateElement.text(BasePage.Date.format(startDate, 'DD MMM HH:mm'));
    }
};

/**
 * Show Broadcasts only for UK
 *
 * @returns {void}
 *
 * @private
 */

var showBroadcast = function () {
    const isJurisdictionCom = window.whStatic.config.jurisdiction === '.com';

    if (isJurisdictionCom && window.WH.sportsbook.broadcasts) {
        var broadcast = content.find('.event-broadcast');
        if (broadcast && broadcast.length !== 0) {
            var broadcasts = Object.values(window.WH.sportsbook.broadcasts).find(b => b.regionId === '35');
            if (broadcasts) {
                broadcast.get(0).innerHTML = ', ' + broadcasts.channelName;
            }
        }
    }
};

/**
 * Binds to a market collection
 *
 * @returns {void}
 *
 * @private
 */
var bindMarketCollectionsMenu = function () {

    // Send message to open BuildYourOdds modal
    if (deps.preferencesProvider.getLocale() === 'en-gb') {
        BasePage.jQuery('#byo-link, #bb-banner').on('click', function () {
            window.WH.messageBus.publish(MessageBusTopics.OPEN_BUILD_YOUR_ODDS_MODAL);
        });
    } else {
        BasePage.jQuery('#byo-link, #byo-btn').on('click', function () {
            window.WH.messageBus.publish(MessageBusTopics.OPEN_BUILD_YOUR_ODDS_MODAL);
        });
    }

    BasePage
        .jQuery('#marketCollectionsMenu')
        .on('click', '.filter-list__item a:not(.-active)', clickHandler);
};

/**
 * Clear intervals and subscriptions and resubscribe new stuff
 *
 * @returns {void}
 *
 * @private
 */
var initNewEventData = function () {

    /**
     * Init elements' classes & variables
     *
     * @return {void}
     *
     * @private
     */
    var initEntities = function () {

        var id = menuHandler.getSelectedMenuId();

        clearAll();
        BasePage
            .jQuery('#marketCollectionsMenu .filter-list__item a.-active')
            .removeClass('-active');
        BasePage
            .jQuery('#marketCollectionsMenu .filter-list__item a[data-marketcollectionid=' + id + ']')
            .addClass('-active');
        menuHandler.udpateLoadingStatus(false);
    };

    /**
     * Init subscriptions if necessary
     *
     * @return {void}
     *
     * @private
     */
    var initSubscriptions = function () {

        push.handledNodes = [];

        if (window.WH.sportsbook.eventByMarketCollectionException !== true) {
            // Re bind and subscribe.
            initModel(true);
            initViews(deps, content);
            // subscribe hidden entities to push
            BasePage.manageHiddenEntities(deps);
        }

        push.subscribeBannersToPush();

        push.clearPushUpdateSubscriptions();

        push.batchSubscribe(window.WH.sportsbook.model.events[window.WH.sportsbook.eventId]);
    };

    initEntities();
    initSubscriptions();
    updateErrorVisibility();

    pagination.initializePagination();
};

/**
 * Generates the ajax request and binds the callbacks
 *
 * @params {Integer} collectionId   id of the collection to
 *                                  request new content for
 *
 * @return {void}
 *
 * @private
 */
var requestNewContent = function (collectionId) {

    menuHandler.udpateLoadingStatus(true);

    collectionId = (typeof collectionId === 'undefined') ?
        menuHandler.getSelectedMenuId() :
        collectionId;

    /**
     * Handle error in request
     *
     * @return {void}
     *
     * @private
     */
    var handleError = function () {

        menuHandler.udpateLoadingStatus(false);
    };

    BasePage.jQuery('div.event-page-error').addClass('displayNone');

    push.unsubscribeBannersFromPush();

    if (deps && deps.pageCommon) {
        deps.pageCommon.reloadSnippet(
            {
                'requestURL': URLProvider.event.getEventPageByCollection({
                    locale: deps.preferencesProvider.getLocale(),
                    sport: window.WH.sportsbook.sport,
                    selectedId: collectionId,
                    eventId: window.WH.sportsbook.eventId,
                }),
            },
            '#eventEntity',
            {
                afterSuccess: initNewEventData,
                afterError: handleError,
            },
            'div.mask'
        );
    }

};

/**
 * Reloads the events for a collection id
 *
 * @params {Integer} collectionId   id of the collection to
 *                                  request new content for
 *
 * @returns {void}
 *
 * @private
 */
var reloadEventByCollectionId = function (collectionId) {

    requestNewContent(collectionId);
};

/**
 * Checks for available collections
 *
 * @returns {boolean}
 *
 * @private
 */
var collectionsAvailable = function () {

    return (content.find('#marketCollectionsMenu .filter-list__item a').length > 0);
};

var isEventDisplayed = function () {

    var elem = content.find('#' + window.WH.sportsbook.eventId);

    return !!(elem.length > 0 && !elem.hasClass('displayNone'));
};

/**
 * Check if the menu contains any collection in order to display or hide it
 *
 * @return {void}
 *
 * @private
 */
var updateMenuVisibility = function () {

    content.find('#marketCollectionsMenu')
        .toggleClass('displayNone', !collectionsAvailable());
};

/**
 * Display an event level error
 *
 * @returns {void}
 *
 * @private
 */
var displayEventLevelError = function () {

    BasePage
        .jQuery('div.event-page-error')
        .toggleClass(
            'displayNone',
            isEventDisplayed() || collectionsAvailable()
        );
};

/**
 * Display a market level error
 *
 * @returns {void}
 *
 * @private
 */
var displayMarketLevelError = function () {

    BasePage
        .jQuery('div.markets-page-error')
        .toggleClass(
            'displayNone',
            isEventDisplayed() || !collectionsAvailable()
        );
};

/**
 * Updates the error visibility state
 *
 * @returns {void}
 *
 * @private
 */
var updateErrorVisibility = function () {

    displayEventLevelError();
    displayMarketLevelError();

    updateMenuVisibility();
};

/**
 * Handles the click on a menu item
 *
 * @param   {Integer} selectingId   id of the selection to handle
 * @param {string} selectingName name of market collection to handle
 *
 * @returns {void}
 *
 * @private
 */
var handleMenuItemClick = function (selectingId, selectingName) {

    menuHandler.updateSelectedMenuId(selectingId);

    if (MinuteMarkets.isMinuteMarketsCollection(selectingName)) {

        minuteMarkets.handleMinuteMarketsCollection();
    } else {

        reloadEventByCollectionId(selectingId);
    }
};

/**
 * Action fired on clicking the Load More button
 *
 * @returns {void}
 *
 * @private
 */
var showMoreSelections = function () {

    var hiddenSelections = BasePage.jQuery(this).parent().parent()
        .find('.cpt-goalscorer__player.displayNone');

    if (hiddenSelections.length) {
        _.take(hiddenSelections, 5).map(show);
    }

    hideShowMoreButton(this);
};

/**
 * If needed, hide Load More button
 *
 * @param {Object} element
 *
 * @returns {void}
 *
 * @private
 */
var hideShowMoreButton = function (element) {

    if (BasePage.jQuery(element).parent().parent().find('.cpt-goalscorer__player.displayNone').length === 0) {
        BasePage.jQuery(element).parent().addClass('displayNone');
    }
};

/**
 * Clears all intervals
 *
 * @returns {void}
 *
 * @private
 */
var clearAll = function () {

    BasePage.IntervalManager.clearAll();
};

/**
 * Updates and loads an element
 *
 * @param   {Object} element    jQuery element
 *
 * @returns {void}
 *
 * @private
 */
var updateAndLoad = function (element) {

    if (element &&
        typeof element.data('marketcollectionid') !== 'undefined') {
        handleMenuItemClick(
            element.data('marketcollectionid'),
            element.data('marketcollectionname')
        );
    }
};

/**
 * Handles a click event
 *
 * @returns {boolean}
 *
 * @private
 */
var clickHandler = function () {

    var element = BasePage.jQuery(this);

    MinuteMarkets.hideEntryPoint();
    BasePage.jQuery('#eventEntity').removeClass('displayNone');

    if (menuHandler.getLoadingStatus()) {
        return false;
    }

    pagination.destroy();
    updateAndLoad(element);
};

/**
 * Checks if a market will be lazy loaded
 *
 * @param {string} marketId market id
 *
 * @returns {boolean}
 *
 * @private
 */
var isLazyLoadedMarket = function (marketId) {

    var market = window.WH.sportsbook.model.markets[marketId];

    return market && market.lazy;
};

/**
 * Adds an in play market from the model to the DOM
 *
 * @param {Object} market Market
 *
 * @returns {void}
 *
 * @private
 */
var addSubscribedMarket = function (market) {

    if (market.hasOwnProperty('hasInPlay') &&
        market.hasInPlay.toString() === 'true' &&
        content.find('#' + market.id).length === 0) {
        var processedMarket = EventDataProcessor.processMarket(market);
        push.marketToView(processedMarket);
        push.checkForCollectionReinit(market);
    }
};

var push = {

    /** Whether we disconnected from the Push Adapter after it had been initialized **/
    disconnected: false,

    /**
     * Required props to display new markets or selections
     *
     * @type {Object}
     */
    requiredProps: {

        /**
         * Required props for markets
         *
         * @type {Array}
         */
        markets: [
            'id',
            'name',
            //'order',
            'selections',
            'displayed',
            'active',
        ],

        /**
         * Required props for selections
         *
         * @type {Array}
         */
        selections: [
            'id',
            'name',
            'status',
            'displayed',
        ],
    },

    /**
     * Array of nodes already handled
     *
     * @type {Array}
     */
    handledNodes: [],


    /**
     * Subscribe a set of entities (events, markets, etc)
     * to PUSH updates
     *
     * @param   {Object} set An object containing entities
     *                       such as markets, selections, events
     *
     * @returns {void}
     *
     * @private
     */
    subscribeSetToPush: function (set) {

        _.each(set, push.subscribeEntityToPush);
    },

    /**
     * Unsuscribes a set of entities (events, markets, selections)
     *
     * @param {Object} set Object containing entities
     *
     * @returns {void}
     *
     * @private
     */
    unsubscribeSetFromPush: function unsubscribeSetFromPush(set) {

        _.each(set, push.unsubscribeEntityFromPush);
    },

    /**
     * Suscribes a single entity to PUSH updates
     *
     * @param {Object} entity Object with pds data
     *
     * @returns {void}
     *
     * @private
     */
    subscribeEntityToPush: function subscribeEntityToPush(entity) {

        if (entity && entity.id && !entity.id.match(/OB_TY/g) &&
            deps && deps.pageCommon && deps.pageCommon.DomPushAdapter.subscribe(entity, function () {
            })) {
            deps.pageCommon.PushAdapter.subscribe(entity.pdsTopic);
        }
    },

    /**
     * Unsuscribes a single entity from PUSH updates
     *
     * @param {Object} entity Object with pds data
     *
     * @returns {void}
     *
     * @private
     */
    unsubscribeEntityFromPush: function unsubscribeEntityFromPush(entity) {

        if (entity && entity.id && !entity.id.match(/OB_TY/g)) {

            deps.pageCommon.DomPushAdapter.unsubscribe(entity);
            deps.pageCommon.PushAdapter.unsubscribe(entity.pdsTopic);
        }
    },

    /**
     * Unsuscribes the entities contained in the banners from PUSH updates.
     * Clean up the global object modelBoxBanner
     *
     * @returns {void}
     *
     * @private
     */
    unsubscribeBannersFromPush: function unsubscribeBannersFromPush() {

        _.each(
            BasePage.ModelComposer.convertHashModelBox(window.WH.sportsbook.modelBoxBanner),
            function (bannersModel) {
                _.each(bannersModel, push.unsubscribeSetFromPush);
            }
        );

        window.WH.sportsbook.modelBoxBanner = {};

    },

    /**
     * Suscribes the entities contained in the banners to PUSH updates
     *
     * @returns {void}
     *
     * @private
     */
    subscribeBannersToPush: function subscribeBannersToPush() {

        _.each(
            BasePage.ModelComposer.convertHashModelBox(window.WH.sportsbook.modelBoxBanner),
            function (bannersModel) {
                _.each(bannersModel, push.subscribeSetToPush);
            }
        );
    },

    /**
     * Disconnects from push messages
     *
     * @return  {void}
     *
     * @private
     */
    disconnect: function () {

        deps.pageCommon.PushAdapter.disconnect();
        push.handleEventSubscriptions({ value: false });
        push.disconnected = true;
    },
    /**
     * Reconnects to push messages after disconnect
     *
     * @return  {void}
     *
     * @private
     */
    reconnect: function () {

        if (push.disconnected) {

            deps.pageCommon.PushAdapter.connect();
            push.handleEventSubscriptions({ value: true });
            push.disconnected = false;
        }
    },
    /**
     * Handles the event subscriptions and delegates to the appropriate push methods
     *
     * @params  {String} message
     *
     * @return  {void}
     *
     * @private
     */
    handleEventSubscriptions: function (message) {

        if (message.value === false) {
            push.clearPushUpdateSubscriptions();
        } else {
            push.subscribeVisibleEntities();
        }
    },

    /**
     * Handles the display messages
     *
     * @params  {String} message
     *
     * @return  {void}
     *
     * @private
     */
    displayUpdateCase: function (message) {

        if (message.id === window.WH.sportsbook.eventId) {
            push.handleEventSubscriptions(message);
            updateErrorVisibility();
        }
    },

    /**
     * Subscribe events to live Scorer
     *
     * @returns {void}
     *
     * @private
     */
    subscribeToLiveScore: function () {

        var model = deps.sportsDataRepository.getModel();

        _.each(model.events, function (event, eventId) {
            if (event.isInPlay && window.WH.sportsbook.squizScoreboard === true) {
                LivescoreDiffusionAdapter.subscribe(
                    BasePage.getSportEnSlug(event),
                    eventId,
                    ScoreboardAdapter.api(content).update
                );
            }
        });
    },

    /**
     * Subscribe events to enhanced odds
     *
     * @returns {void}
     *
     * @private
     */
    subscribeToEnahnacedOdds: function () {

        var model = deps.sportsDataRepository.getModel();

        _.each(model.events, (event, eventId) => {
            if (event.hasEnhancedOdds) {
                EnhancedOddsDiffusionAdapter.subscribe(
                    eventId,
                    EventViewAdapter.wasPrice(eventId, content).update
                );
            }
        });
    },

    /**
     * Subscribes to a visible entity
     *
     * @param   {Object}    entity
     *
     * @returns {void}
     *
     * @private
     */
    subscribeVisibleEntity: function (entity) {

        if (entity && entity.id && !entity.id.match(/OB_TY/g)) {

            window.setTimeout(function () {

                if (deps && deps.pageCommon) {

                    deps.pageCommon.DomPushAdapter.subscribe(
                        entity,
                        push.DomPushAdapterCallback
                    );

                    deps.pageCommon.PushAdapter.subscribe(entity.pdsTopic + '/OB_MA.*');
                    _.each(
                        window.WH.sportsbook.model.selections,
                        push.subscribeEntityToPush
                    );
                }
            });
        }
    },

    /**
     * Subscribe all the visible entities to push messages and dom push messages
     *
     * @return {void}
     *
     * @private
     */
    subscribeVisibleEntities: function () {

        var model = deps.sportsDataRepository.getModel();

        if (model.events.hasOwnProperty(window.WH.sportsbook.eventId)) {
            push.batchSubscribe(model.events[window.WH.sportsbook.eventId]);
        }
    },

    /**
     * Does a batch subscribe for a given topic
     *
     * @param   {Object}    entity
     *
     * @returns {void}
     *
     * @private
     */
    batchSubscribe: function (entity) {

        if (entity && entity.hasOwnProperty('pdsTopic') && entity.pdsTopic) {

            // Remove double slashes from the topic if they are present
            entity.pdsTopic = entity.pdsTopic.replace(/\/{2,}$/g, '');

            // AJAX-343
            window.setTimeout(deps.pageCommon.PushAdapter.subscribe.bind(this, entity.pdsTopic));

            push.subscribeVisibleEntity(entity);
        }
    },

    /**
     * Clears the push update subscriptions
     *
     * @returns {void}
     *
     * @private
     */
    clearPushUpdateSubscriptions: function () {

        deps.pageCommon.PushAdapter.unsubscribeAll();
        deps.pageCommon.DomPushAdapter.unsubscribeAll();
    },

    DomPushAdapterCallbacks: {
        'displayed': 'displayUpdateCase',
        'betInRun': 'betInRunUpdateCase',
    },

    /**
     * This function will handle the DomManipulator messages regarding changes in DOM
     *
     * @param   {Object} message  Message to push
     *
     * @returns {void}
     *
     * @private
     */
    DomPushAdapterCallback: function (message) {

        if (message.lazy === true) {
            push.handleNewMessage(message);
        } else {
            push.messageToAction(message);
        }
    },

    /**
     * Performs an action based on the received message
     *
     * @param {Object} message
     *
     * @returns {void}
     *
     * @private
     */
    messageToAction: function (message) {

        if (push.DomPushAdapterCallbacks.hasOwnProperty(message.attribute)) {
            push[push.DomPushAdapterCallbacks[message.attribute]](message);
        }
    },

    /**
     * Updates an event
     *
     * @params  {String} message
     *
     * @return  {void}
     *
     * @private
     */
    eventUpdate: function (message) {
        const $event = content.find('#' + window.WH.sportsbook.eventId);
        const newValue = message.value.toString();

        // Early-out if there's no state change or the event isn't displayed
        if (newValue === $event.attr('data-betinrun') || !isEventDisplayed()) return;

        // Exponential backoff fetch
        const fetchWithBackoff = (maxRetries, initialDelay, fetchFn, onSuccess, onFail) => {
            let retries = 0, delay = initialDelay;

            const attempt = () => {
                fetchFn()
                    .then(handleResponse)
                    .catch(handleError);
            };

            const handleResponse = (response) => {
                const retryStatuses = [404, 500, 503];

                if (response.status === 200) {
                    return onSuccess(response);
                }

                if (retryStatuses.includes(response.status)) {
                    return retry(`Attempt ${retries + 1}: Received ${response.status}, retrying in ${delay}ms...`);
                }

                onFail(`Unexpected status ${response.status}, stopping retries.`);
            };

            const handleError = (error) => {
                getConsole().error(`Error during fetch: ${error.message}`);
                retry(error.message);
            };

            const retry = (msg) => {
                if (retries++ < maxRetries) {
                    getConsole().warn(msg);
                    setTimeout(attempt, delay);
                    delay *= 2;
                } else {
                    onFail(`Failed after ${maxRetries} attempts.`);
                }
            };

            attempt();
        };

        // Handlers for "true" / "false"
        const handleTrueState = () => {
            fetchWithBackoff(
                6,
                2000,
                () => fetch(push.inPlayUrlGenerator(true)),
                () => {
                    pubSub.publish(MessageBusTopics.INTERNAL_ROUTE, {
                        url: push.inPlayUrlGenerator(),
                        ignoreBackButton: true,
                    });
                },
                (err) => getConsole().warn(err)
            );
        };

        const handleFalseState = () => {
            pubSub.publish(MessageBusTopics.INTERNAL_ROUTE, {
                url: push.inPlayUrlRemover(),
                ignoreBackButton: true,
            });
        };

        // Route to the correct handler
        const processMessage = (val) => {
            if (val === 'true') return handleTrueState();
            if (val === 'false') return handleFalseState();
            getConsole().error(`Unknown state: ${val}`);
        };

        processMessage(newValue);
    },



    /**
     * Appends the /inplay route onto the URL, removing any trailing slashes
     *
     * @return {string} The new URL
     *
     * @private
     */
    inPlayUrlGenerator: (isPartial) => {
        let url = window.location.pathname;
        const { host, protocol } = window.location;

        if (!url.endsWith('/inplay')) {
            url = `${url.replace(/\/$/, '')}/inplay`;
        }

        // if `isPartial` is true, return url w.host/inplay.partial
        return isPartial ? `${protocol}//w.${host}${url}.partial` : url;
    },

    /**
     * Removes the /inplay route from the URL if it exists
     *
     * @return {string} The new URL
     *
     * @private
     */
    inPlayUrlRemover: () => {
        let url = window.location.pathname;
        if (url.endsWith('/inplay')) {
            url = `${url.replace('/inplay', '')}`;
        }
        return url;
    },

    /**
     * Adds valid markets to the DOM, from
     * the model
     *
     * @returns {void}
     *
     * @private
     */
    addValidMarkets: function () {
        _.each(
            deps.sportsDataRepository.getModel().markets,
            addSubscribedMarket
        );

        if (content.find('#markets-container').is(':empty')) {
            updateAndLoad(
                content.find('a[data-marketcollectionid]:visible:first')
            );
        }
    },

    /**
     * Handles the dom push message containing an betInRun attribute
     *
     * @params  {String} message
     *
     * @returns  {void}
     *
     * @private
     */
    betInRunUpdateCase: function (message) {

        if (message.nodeType === 'events') {
            push.eventUpdate(message);
        }
    },

    /**
     * Set Entity Ancestors
     *
     * @param {Object} message
     *
     * @returns {Array}
     *
     * @private
     */
    setEntityAncestors: function (message) {

        // Split topic to get ancestors
        message.entity.ancestors = message.topics.current.split('/');

        // remove 'PDS' keyword
        message.entity.ancestors.shift();

        // remove the element id from it's own ancestors
        message.entity.ancestors.pop();

        return message.entity.ancestors;
    },

    /**
     * Check if provided marketGroup id belongs to active market group collection
     * If marketGroup is missing (was missing in message) return true
     *
     * @param {String} [marketGroup] Market group id
     *
     * @returns {Boolean}
     */
    isMarketGroupInCurrentCollection: function isMarketGroupInCurrentCollection(marketGroup) {

        if (marketGroup) {

            return _.chain(window.WH.sportsbook.collections.selected.markets)
                .map('id')
                .flatten()
                .contains(marketGroup)
                .value();
        } else {

            return true;
        }

    },

    /**
     * Checks that the event and market both have displayed = true
     *
     * @param {Object} entity Market entity from diffusion message
     *
     * @returns {boolean} Whether or not both market and event are displayed
     *
     * @private
     */
    isMarketAndEventDisplayed: function isMarketAndEventDisplayed(entity) {

        var event = deps.sportsDataRepository.getModel().events[entity.ancestors[0]];

        return !!(event &&
            entity.displayed.toString() === event.displayed.toString() &&
            event.displayed.toString() === 'true');

    },

    /**
     * Checks that the event and market are both unsettled
     *
     * @param {Object} entity Market entity from diffusion message
     *
     * @returns {boolean} Whether or not both market and event are settled = false
     *
     * @private
     */
    isMarketAndEventUnsettled: function isMarketAndEventSettled(entity) {

        var event = deps.sportsDataRepository.getModel().events[entity.ancestors[0]];

        return !!(event &&
            entity.settled.toString() === event.settled.toString() &&
            event.settled.toString() === 'false');
    },

    /**
     * Handles the processing of new Message being pushed through diffusion
     *
     * @param {Object} message
     *
     * @returns {void}
     *
     * @private
     */
    handleNewMessage: function handleNewMessage(message) {

        if (!_.includes(push.handledNodes, message.entity.id) &&
            message.entity.displayed && message.entity.displayed === 'true') {

            push.handleNewNode(message);
        }
    },

    /**
     * Handle new market message
     *
     * @param {Object} message
     *
     * @returns {void}
     */
    handleNewMarketEntity: function (message) {

        if (push.isMarketGroupInCurrentCollection(message.entity.marketGroupId)) {

            push.addNewMarket(message.entity);
        }

        _.each(marketGroupsCollections[message.entity.marketGroupId], push.enableCollectionMenuItem);
    },

    /**
     * Handles the processing of new Messages being pushed through diffusion
     *
     * @param {Object} message
     *
     * @returns {void}
     *
     * @private
     */
    handleNewNode: function handleNewNode(message) {

        message.entity.ancestors = push.setEntityAncestors(message);

        if (push.isMarketEntity(message.nodeType, message.entity)) {

            push.checkValidityAndHandleMarket(message);
        } else {

            push.handledNodes.push(message.entity.id);
            push.handleNewSelections(message);
        }
    },

    /**
     * Checks market is valid before proceeding with adding the new market
     *
     * @param {Object} message A diffusion push message
     *
     * @returns {void}
     *
     * @private
     */
    checkValidityAndHandleMarket: function checkValidityAndHandleMarket(message) {

        if (push.isMarketAndEventDisplayed(message.entity) &&
            push.isMarketAndEventUnsettled(message.entity) &&
            push.checkMarketEventSameStatus(message.entity.ancestors[0], message.entity.hasInPlay)) {

            push.handledNodes.push(message.entity.id);
            push.handleNewMarketEntity(message);
        }
    },

    /**
     * Handles new pushed selections
     *
     * @param {Object} message
     *
     * @returns {boolean}
     *
     * @private
     */
    handleNewSelections: function (message) {

        // if we already have a shallow model skip as it will be lazy loaded
        if (message.nodeType === 'selections' &&
            !isLazyLoadedMarket(message.entity.ancestors[1])) {

            deps.sportsDataRepository.addSelectionToModel(
                message.entity.ancestors[1],
                message.entity
            );

            return true;
        }

        return false;
    },

    /**
     *
     * @param {String} collectionId
     *
     * @returns {void}
     */
    enableCollectionMenuItem: function enableCollectionMenuItem(collectionId) {

        jQuery('#marketCollectionsMenu [data-marketcollectionid="' + collectionId + '"]')
            .removeClass('displayNone')
            .parent()
            .removeClass('displayNone');
    },

    /**
     * Check if the entity is market
     *
     * @param  {String} type   entity type. ie: selections or markets
     * @param  {Object} entity new pushed entity to add
     *
     * @returns {Boolean}
     *
     * @private
     */
    isMarketEntity: function isMarketEntity(type, entity) {

        return type === 'markets' && entity.hasOwnProperty('hasInPlay');
    },

    /**
     * Adds a new market to the DOM using an XHR to get the fragment
     *
     * @param {Object} market A market entity object
     *
     * @returns {void}
     *
     * @private
     */
    addNewMarket: function addNewMarket(market, timeoutDebounceTime) {

        var marketId = market.id,
            eventId = market.ancestors[0],
            args = UrlArgsProvider.get(),
            fragmentUrl = URLProvider.event.market({
                eventId: eventId,
                marketId: marketId,
                locale: window.WH.sportsbook.lang,
                sport: args.sport,
            });

        /**
         * Empty handler - we don't want to do anything if the market fragment is unavailable
         *
         * @returns {boolean}
         *
         * @private
         */
        var handleError = function handleError(e) {
            if (e.status === 404) {
                var timeoutTime = timeoutDebounceTime || 32;
                if (timeoutTime <= 256) {
                    var beforePageChangeSubToken;
                    var retryTimeout = setTimeout(function () {
                        window.WH.messageBus.unsubscribe(beforePageChangeSubToken);
                        addNewMarket(market, timeoutTime * 2);
                    }, timeoutTime * 1000);
                    beforePageChangeSubToken = window.WH.messageBus.subscribe(
                        MessageBusTopics.NAVIGATION_BEFORE_PAGE_CHANGE,
                        function () {
                            clearTimeout(retryTimeout);
                            window.WH.messageBus.unsubscribe(beforePageChangeSubToken);
                        });
                }
            }
            return false;
            //market not available
        };

        /**
         * Success handler - If we do get a new fragment, we want to grab the model data from the modelBox,
         * merge it into the model, and subscribe to push updates for the new elements
         *
         * @returns {void}
         *
         * @private
         */
        var handleSuccess = function handleSuccess() {

            var fragmentModel = BasePage
                .ModelComposer.convertHashModel(window.WH.sportsbook.modelBox['marketFragment-' + marketId]);

            window.WH.sportsbook.model.events[eventId].markets.push(marketId);
            window.WH.sportsbook.model.markets[marketId] = fragmentModel.markets[marketId];

            BasePage.jQuery.extend(true, window.WH.sportsbook.model.selections, fragmentModel.selections);

            push.subscribeSetToPush(fragmentModel.markets);
            push.subscribeSetToPush(fragmentModel.selections);

            delete window.WH.sportsbook.modelBox['marketFragment-' + marketId];
        };
        deps.pageCommon.reloadSnippet({ requestURL: fragmentUrl }, '#markets-container',
            { afterSuccess: handleSuccess, afterError: handleError },
            true,
            true,
            true
        );
    },

    /**
     * Sets the timeout to verify if we have all the data from diffusion. This is tricky as some of the
     * selections may still be comming through push messages but there's no way to find out if this is actually
     * the case
     *
     * @param {Object} market
     *
     * @returns {void}
     *
     * @private
     */
    verifyNewNode: function (type, entity) {

        entity.timeout = setTimeout(
            function () {
                push.checkMarketData(type, entity);
            },
            1000
        );
    },

    /**
     * This function check that a event has the same status than the market
     *
     * @param   {String}    eventId             EventID from PDS Event Object
     * @param   {String}   marketHasInPlay      hasInPlay market attribute, string with boolean content
     *
     * @returns {boolean}
     *
     * @private
     */
    checkMarketEventSameStatus: function (eventId, marketHasInPlay) {

        var event = deps.sportsDataRepository.getModel().events[eventId];

        return !!(event && marketHasInPlay.toString() === event.isInPlay.toString());
    },

    /**
     * Checks the market data and clears the timeout if all required data has been pushed
     *
     * @param  {string} type   entity type. ie: selections or markets
     * @param  {Object} entity new pushed entity to add
     *
     * @return {void}
     *
     * @private
     */
    checkMarketData: function (type, entity) {

        var diff = _.difference(push.requiredProps[type], Object.keys(entity));

        push.clearTimeout(entity);

        if (diff.length > 0) {

            // we still don't have all data needed
            push.verifyNewNode(type, entity);
        }
        else {
            push.handlePushedMarket(type, entity);
        }
    },

    /**
     * Clears an entity timeout
     *
     * @param {Object} entity selections or markets
     *
     * @returns {void}
     *
     * @private
     */
    clearTimeout: function (entity) {

        clearTimeout(entity.timeout);
        delete entity.timeout;
    },

    /**
     * Finishes the processing and renders the market template
     *
     * @param  {string} type   entity type. ie: selections or markets
     * @param  {Object} entity new pushed entity to add
     *
     * @return {void}
     *
     * @private
     */
    handlePushedMarket: function (type, entity) {

        if (type === 'markets' &&
            entity.hasOwnProperty('hasInPlay') &&
            push.checkMarketEventSameStatus(entity.ancestors[0], entity.hasInPlay) &&
            BasePage.jQuery('#' + entity.id).length === 0) {

            entity = push.clearDuplicatedSelections(entity);
            // update the model
            entity = push.addMarketToModel(entity);

            // process data
            var processedMarket = EventDataProcessor.processMarket(entity);
            push.handlePushProcessedMarket(processedMarket, entity);
        }
    },

    /**
     * Handles the push market after processing it to avoid re-initting the collections
     *
     * @param {Object} processedMarket The processed market
     * @param {Object} entity The non processed market
     *
     * @returns {void}
     *
     * @private
     */
    handlePushProcessedMarket: function (processedMarket, entity) {

        if (!processedMarket) {

            return;
        }

        push.marketToView(processedMarket);
        push.checkForCollectionReinit(entity);
    },

    /**
     * Pushes a market into view
     *
     * @param {Object} market to be pushed
     *
     * @returns {boolean}
     *
     * @private
     */
    marketToView: function (market) {

        // not valid market
        if (!market) {

            // we should remove this market from the collection
            return false;
        }

        push.addNewMarket(market);
    },

    /**
     * Show Other Markets collection
     *
     * @param {Boolean} show Controls if we show the Other Markets Collection or not
     * @param {Boolean} reinitSwiper reinit Swiper or not
     *
     * @returns {Boolean}
     *
     * @private
     */
    showOtherMarketsCollection: function (show, reinitSwiper) {

        if (show === true) {
            BasePage.jQuery('a[data-marketcollectionid="9999"]',
                BasePage.jQuery('#marketCollectionsMenu')).removeClass('displayNone');

            return true;
        }

        return reinitSwiper;
    },
    
    /**
     * Actions to be taken after the MC check
     *
     * @param {Boolean} isUnassigned Checks if the market belongs to unassigned collection
     * @param {Boolean} reinitSwiper Controls whether we reinit Swiper or not
     *
     * @returns {void}
     *
     * @private
     */
    afterMarketCollectionCheck: function (isUnassigned, reinitSwiper) {

        SelectionViewAdapter.init();

        reinitSwiper = push.showOtherMarketsCollection(isUnassigned, reinitSwiper);

        if (reinitSwiper === true && SwiperNavigation) {

            SwiperNavigation.reInit('marketCollectionsMenu');
        }
    },

    /**
     * Check if the market should trigger collection reinit
     * Find market collection id corresponding to the marketGroupId
     *
     * @param {Object} market
     *
     * @returns {void}
     *
     * @private
     */
    checkForCollectionReinit: function (market) {

        var isUnassigned = true;
        var reinitSwiper = false;
        var marketCollectionWrapper = BasePage.jQuery('#marketCollectionsMenu');

        /**
         * Loop through market collections to find the one
         *
         * @param {Object} singleCollection
         *
         * @returns {void}
         *
         * @private
         */
        var findMarketCollectionId = function (singleCollection) {

            var marketCollection = _.find(singleCollection.markets, { id: market.marketGroupId });

            if (!_.isUndefined(marketCollection)) {
                content.find('a[data-marketcollectionid="' + singleCollection.id +
                    '"]', marketCollectionWrapper).removeClass('displayNone');
                isUnassigned = false;
                reinitSwiper = true;
            }
        };

        _.each(window.WH.sportsbook.fullMarketCollections, findMarketCollectionId);
        push.afterMarketCollectionCheck(isUnassigned, reinitSwiper);
    },

    /**
     * Returns selection ID
     *
     * @param {Object} selection
     *
     * @returns {String}
     *
     * @private
     */
    getSelectionId: function (selection) {

        if (selection.hasOwnProperty('id')) {
            return selection.id;
        }
        return selection;
    },

    /**
     * Clears duplicated selections
     *
     * @param {Object} entity
     *
     * @returns {Object}
     *
     * @private
     */
    clearDuplicatedSelections: function (entity) {

        entity.selections = _.uniq(entity.selections, push.getSelectionId);

        return entity;
    },

    /**
     * Adds a pushed market to the model
     *
     * @param {Object} market pushed market to be added
     *
     * @returns {Object}
     *
     * @private
     */
    addMarketToModel: function (market) {

        var selections = JSON.parse(JSON.stringify(market.selections));

        market = _.extend({}, market, {
            selections: _.uniq(_.map(selections, 'id')),
        });

        deps.sportsDataRepository.addNode(market, 'markets', market.ancestors[0]);

        /**
         * Handles each selection in a market by adding it to the model and returning only it's id
         *
         * @param  {Object} selection to be processed
         *
         * @return {string}           selection id
         *
         * @private
         */
        var handleSelections = function (selection) {

            deps.sportsDataRepository.addNode(selection, 'selections', market.id);
            return selection.id;
        };

        _.each(selections, handleSelections);

        return market;
    },

    /**
     * Callback for the onTopicStatusFunction
     * Removes the topic from DOM
     *
     * @param {TopicStatusMessage} topicStatusMessage TSM from Diffusion
     *
     * @returns {void}
     *
     * @private
     */
    onTopicStatusFunction: function (topicStatusMessage) {

        if (topicStatusMessage.status === 'R') {

            var removedTopic = topicStatusMessage.topic.split('/').pop();

            BasePage.jQuery('#' + removedTopic).remove();
        }
    },

    /**
     * Adds function to call on topic status change
     *
     * @returns {void}
     *
     * @private
     */
    addOnTopicStatusFunction: function () {

        /**
         * Add handler for the onTopicStatusFunction
         *
         * @param {TopicStatusMessage} msg TSM from Diffusion
         *
         * @returns {void}
         *
         * @private
         */
        window.WH.sportsbook.onTopicStatusFunction = function (msg) {

            push.onTopicStatusFunction(msg);
        };
    },
};

var minuteMarkets = {

    /** References to messageBus subscriptions of minute markets **/
    mmSubscriptions: {},

    /**
     * Callback that gets executed when minute market app is launched
     *
     * @return  {void}
     *
     * @private
     */
    onMinuteMarketsLaunched: function onMinuteMarketsLaunched() {

        removeScoreboard();
        push.disconnect();
    },

    /**
     * Callback that gets executed when minute market app is closed
     *
     * @return  {void}
     *
     * @private
     */
    onMinuteMarketsClosed: function onMinuteMarketsClosed() {

        restoreScoreboard();
        push.reconnect();
    },

    /**
     * Listens to MM messageBus events if the module is enabled
     *
     * @return  {void}
     *
     * @private
     */
    subscribeToMinuteMarketsLifecycle: function subscribeToMinuteMarketsLifecycle() {

        minuteMarkets.mmSubscriptions.show = pubSub.subscribe(
            MessageBusTopics.MINUTE_MARKETS_SHOW, minuteMarkets.onMinuteMarketsLaunched
        );
        minuteMarkets.mmSubscriptions.close = pubSub.subscribe(
            MessageBusTopics.MINUTE_MARKETS_DESTROY, minuteMarkets.onMinuteMarketsClosed
        );
    },

    /**
     * Destroys MM messageBus listeners if the module is enabled
     *
     * @return  {void}
     *
     * @private
     */
    unsubscribeFromMinuteMarketsLifecycle: function unsubscribeFromMinuteMarketsLifecycle() {

        pubSub.unsubscribe(minuteMarkets.mmSubscriptions.show);
        pubSub.unsubscribe(minuteMarkets.mmSubscriptions.close);
    },

    handleMinuteMarketsCollection: function handleMinuteMarketsCollection() {

        MinuteMarkets.mountEntryPoint();
        BasePage.jQuery('#eventEntity').addClass('displayNone');
        BasePage.jQuery('#marketCollectionsMenu .filter-list__item a.-active').removeClass('-active');
    },
};

/**
 * Remove the scoreboard.
 *
 * @returns {void}
 * @private
 */
var removeScoreboard = function () {

    if (window.WH && window.WH.sportsbook && window.WH.sportsbook.scoreboard) {

        window.WH.sportsbook.scoreboard.destroy();
    }
};

/**
 * Restores the scoreboard after it's been destroyed
 *
 * @returns {void}
 * @private
 */
var restoreScoreboard = function () {

    if (window.WH && window.WH.ScoreboardIntegration && window.WH.scoreboardContainer) {

        window.WH.sportsbook.scoreboard = new window.WH.ScoreboardIntegration(
            window.WH.scoreboardContainer
        );
    }
};

/**
 * Published a message indicating whether the event page is loaded or not
 *
 * @param isLoaded
 *
 * @returns {void}
 *
 * @private
 */
var publishLoaded = function (isLoaded) {

    if ($scoreboards && $scoreboards.length > 0) {

        pubSub.publish(MessageBusTopics.EVENTS_PAGE_LOADED, isLoaded);
    }
};

/**
 * Creates a map object between market groups and all collections
 *
 * [marketGroupId]: [...marketCollectionIds]
 *
 * @returns {void}
 */
var setMarketCollectionGroups = function setMarketCollectionGroups() {

    var clearDuplicates = function clearDuplicates(key) {

        result[key] = _.uniq(result[key]);
    };

    var result = {};

    _.each(window.WH.sportsbook.fullMarketCollections, function (marketCollection) {

        _.each(_.flatten(_.map(marketCollection.markets, 'id')), function (marketGroup) {

            result[marketGroup] = result[marketGroup] || [];
            result[marketGroup].push(marketCollection.id);
        });

    });

    _.each(_.keys(result), clearDuplicates);

    marketGroupsCollections = result;
};

let cashoutCounterSubscriptionId;

const isCashoutOnEventPageEnabled = () => (window.whStatic.config.cashoutOnEventPage || {}).enabled;

/**
 * Method to be executed when page is ready to
 * launch cashout on event page
 *
 * @returns {void}
 *
 * @private
 */
const initCashoutOnEventPage = () => {
    if (isCashoutOnEventPageEnabled()) {
        const id = BasePage.jQuery('#cashout-on-event-page-entry');
        const eventId = window.WH.sportsbook.eventId;

        cashoutCounterSubscriptionId = pubSub.subscribe({
            topic: MessageBusTopics.CASHOUT_ON_EVENT_PAGE_COUNTER,
            callback: ({ data }) => updateCashoutCounter(data || 0),
        });

        pubSub.publishSticky(MessageBusTopics.EVENT_PAGE_OPENED, eventId);

        BasePage.jQuery('#marketCollectionsMenu').addClass('cashout-on-event-page-parent');

        id.addClass('cashout-on-event-page-entry').addClass('closed').removeClass('displayNone');

        id.click((e) => {
            if (id.hasClass('opened')) {
                id.addClass('closed').removeClass('opened');
                hide('#cashout-on-event-page-content');
            } else {
                id.addClass('opened').removeClass('closed');
                pubSub.publish(MessageBusTopics.CASHOUT_ON_EVENT_PAGE_SHOW, {
                    contentId: 'cashout-on-event-page-content',
                    eventId,
                });
                show('#cashout-on-event-page-content');
            }
            e.preventDefault();
            e.stopImmediatePropagation();
        });
    }
};

const updateCashoutCounter = (counter) => {
    BasePage.jQuery('#cashout-on-event-page-counter').text(counter);
};

const destroyCashoutOnEventPage = () => {
    if (isCashoutOnEventPageEnabled()) {
        pubSub.publish(MessageBusTopics.EVENT_PAGE_OPENED, null);
        pubSub.unsubscribe(cashoutCounterSubscriptionId);
    }
};

/**
 * Method to be executed before the page changes.
 * Destroy all the references created to the minute markets component
 * and lifecycle inside the application
 *
 * @returns {void}
 *
 * @private
 */
var beforePageChange = function () {

    stopAccaWidget();
    push.reconnect();
    MinuteMarkets.destroy();
    unSubscribePageLifecycleSubscriptions();
    minuteMarkets.unsubscribeFromMinuteMarketsLifecycle();
    destroyCashoutOnEventPage();
};


/**
 * Contains functions exposed by this component
 * as an API
 *
 * @type {Object}
 *
 * @public
 */
var api = {

    internals: function () {

        return {
            deps: deps,
            content: content,
            push: push,
        };
    },

    /**
     * Function used to get the content url
     *
     * @param   {Object} args       Page arguments
     *
     * @returns {string}
     *
     * @public
     */
    getContentURL: function (args) {

        /**
         * getEventParam
         */
        const getEventParam = () => (args.eventName) ? `/${args.eventName}` : '';

        /**
         * getInplayParam
         */
        const getInplayParam = () => (args.inPlay) ? '/inplay' : '';

        return `/betting/${args.locale}/${args.sport}/OB_EV${args.eventId}${getEventParam()}${getInplayParam()}`;
    },

    /**
     * Function called after page is ready
     *
     * @param   {Object} deps       Object that contains page
     *                              dependencies
     *
     * @returns {void}
     *
     * @public
     */
    postReady: function (deps, content, args) {

        stopAccaWidget();
        setMarketCollectionGroups();

        initCashoutOnEventPage();

        initModel(false);
        BodyStateAdapter.init(BasePage.jQuery, 'events', args.sport);

        initMarketCollectionsMenu();

        if (deps.ready) {
            deps.ready('event');
        }

        (function () {

            push.handledNodes = [];

            $scoreboards = BasePage.jQuery('#scoreboard_frame');
            publishLoaded(true);
            // Init swiper
            SwiperNavigation.init('marketCollectionsMenu');
        })();
    },

    /**
     * Function called after rendering the page
     *
     * @param   {Object} deps       Object that contains page
     *                              dependencies
     * @param   {Object} content    jQuery object that contains
     *                              this page
     *
     * @returns {void}
     *
     * @public
     */
    postRender: function (deps, content) {

        if (window.WH.areNewBannersOnEventPageEnabled) {
            renderNewBanners('event');
        }

        initInternals(deps, content);

        handleSubscriptions();

        subscribePageLifecycleSubscriptions();

        (function () {
            initDate();
            showBroadcast();
            menuHandler.updateFromDOM();
        })();

        //just setting the event class, no need to 'switch tab' since that will also set the active classes
        //on the sidebar
        BasePage.addMainContainerClass('event');

        pagination.initializePagination();

        SelectionViewAdapter.init();

        push.subscribeBannersToPush();

        MinuteMarkets.init(
            window.WH.sportsbook,
            BasePage.jQuery('#minute-markets-access-point')
        );

        var activeCollection = BasePage.jQuery('#marketCollectionItemsList .races-buttons-list__button--active');

        if (MinuteMarkets.isMinuteMarketsCollection(activeCollection.data('marketcollectionname'))) {

            minuteMarkets.handleMinuteMarketsCollection();
        }

        HiddenMarketsWidget.start();
        if (getBuilderReady() && !checkIfSpain()) {
            betBuilderListener();
        }
    },

    /**
     * Function used to bind event handles to
     * various buttons/links on the page
     *
     * @param   {Object} content    jQuery object containing
     *                              the page content
     * @param   {Object} deps       Page dependencies
     *
     * @returns {void}
     *
     * @public
     */
    bindNavigation: function (content, deps) {

        initInternals(deps, content);
        bindMarketCollectionsMenu();

        showMatches('.navMatches');

        showMoreButton();
    },

    /**
     * Changes rendered state flag to true
     *
     * @public
     */
    setRendered: function () {

        pubSub = window.WH.messageBus;
        rendered = true;
    },

    /**
     * Changes rendered state flag to false
     *
     * @public
     */
    setUnrendered: function () {

        if (rendered) {
            publishLoaded(false);
        }

        removeScoreboard();

        HiddenMarketsWidget.stop();

        content = null;
        deps = null;
        $scoreboards = null;

        BasePage.setObjectValues(pagination, {
            baseLink: null,
            request: null,
            rowIds: [],
        });

        stopAccaWidget();
        rendered = false;
    },

    /**
     * Returns the value of the internal
     * rendered state flag
     *
     * @returns {Boolean} Returns true or false
     *
     * @public
     */
    isRendered: function () {
        return rendered;
    },

    /**
     * Slug to identify the current page
     */
    id: 'event',
};

export default api;
